import type { LiveWorkflow } from './diff.js';
import { inputValue, strRecord, yamlScalar } from './pull-values.js';

export type PullInput = {
  kind: 'value' | 'credential';
  name: string;
  defaultValue: string;
};

export type PullScenario = {
  name: string;
  description?: string;
  isDefault: boolean;
  includeInSuite: boolean;
  values: Record<string, string>;
  credentials: Record<string, string>;
};

export function readInputs(raw: unknown): PullInput[] {
  if (!Array.isArray(raw)) return [];
  const out: PullInput[] = [];
  for (const i of raw) {
    if (!i || typeof i !== 'object') continue;
    const rec = i as Record<string, unknown>;
    if (typeof rec.name !== 'string') continue;
    out.push({
      kind: rec.kind === 'credential' ? 'credential' : 'value',
      name: rec.name,
      defaultValue: typeof rec.defaultValue === 'string' ? rec.defaultValue : '',
    });
  }
  return out;
}

export function renderInputsSection(inputs: PullInput[]): string[] | null {
  if (inputs.length === 0) return null;
  const lines = ['## Inputs'];
  for (const i of inputs) {
    const prefix = i.kind === 'credential' ? 'credential ' : '';
    lines.push(`- ${prefix}${i.name}: ${inputValue(i.defaultValue)}`);
  }
  lines.push('');
  return lines;
}

export function readScenarios(raw: unknown): PullScenario[] {
  if (!Array.isArray(raw)) return [];
  const out: PullScenario[] = [];
  for (const s of raw) {
    if (!s || typeof s !== 'object') continue;
    const rec = s as Record<string, unknown>;
    if (typeof rec.name !== 'string') continue;
    out.push({
      name: rec.name,
      description: typeof rec.description === 'string' ? rec.description : undefined,
      isDefault: rec.isDefault === true,
      includeInSuite: rec.includeInSuite !== false,
      values: strRecord(rec.values),
      credentials: strRecord(rec.credentials),
    });
  }
  return out;
}

export function renderScenariosSection(
  scenarios: PullScenario[],
  skipReasons: Record<string, string> = {}
): string[] | null {
  if (scenarios.length === 0) return null;
  const lines = ['## Scenarios', ''];
  for (const s of scenarios) {
    let heading = `### ${s.name}`;
    const skipReason = skipReasons[s.name];
    if (s.isDefault) heading += ' *(default)*';
    else if (!s.includeInSuite) {
      heading += skipReason ? ` *(skip: ${skipReason})*` : ' *(skip)*';
    }
    lines.push(heading);
    if (s.description) lines.push(`- description: ${s.description}`);
    if (Object.keys(s.values).length > 0) {
      lines.push('- values:');
      for (const [k, v] of Object.entries(s.values)) lines.push(`    ${k}: ${yamlScalar(v)}`);
    }
    if (Object.keys(s.credentials).length > 0) {
      lines.push('- credentials:');
      for (const [k, v] of Object.entries(s.credentials))
        lines.push(`    ${k}: ${yamlScalar(v)}`);
    }
    lines.push('');
  }
  return lines;
}

/**
 * Render the `## Declared Outputs` section from a seed workflow's
 * `seedOutputsJson` rows. Each entry becomes a top-level bullet (the dataKey)
 * with indented sub-bullets for label/property/description. `credentialOutput`
 * configs surface as a "preserved by server" HTML comment under the entry so
 * the author knows there's UI-authored config they shouldn't try to
 * round-trip through the spec yet.
 */
export function renderDeclaredOutputs(outputs: LiveWorkflow['seedOutputs']): string[] | null {
  if (!outputs || outputs.length === 0) return null;
  const lines = ['## Declared Outputs', ''];
  for (const o of outputs) {
    lines.push(`- ${o.dataKey}`);
    const label = o.displayLabel ?? o.name;
    if (label && label !== o.dataKey) lines.push(`  - label: ${yamlScalar(label)}`);
    if (o.propertyName) lines.push(`  - property: ${yamlScalar(o.propertyName)}`);
    if (o.description) lines.push(`  - description: ${o.description}`);
    if (o.credentialOutput !== undefined) {
      lines.push('  <!-- credentialOutput config preserved by the server; edit in UI for now -->');
    }
    lines.push('');
  }
  // Keep the blank line that separates this section from Journey, but avoid
  // accumulating more than one if future callers append an empty entry.
  while (lines.length > 0 && lines[lines.length - 1] === '' && lines[lines.length - 2] === '') {
    lines.pop();
  }
  return lines;
}
